import React, { useState } from "react";
import { ComponentInstanceSummary } from "../../../api/models/component-instance-summary";
import { formatMessage } from "../../../utils/helpers/LanguageHelper";
import { generateIdentifier } from "../../../utils/helpers/RobotFramework";
import { CAPTION_SHOW_MORE } from "../outlineCaptions";
import { InstanceRow } from "./InstanceRow";

// Fixed by design (U10, FR-008): the reveal page size is the specified constant, never a configurable setting.
const PAGE_SIZE = 20;

export interface InstanceListProps {
  /**
   * One source-document group's instances (T3 `ComponentInstanceSummary[]`), already in **document order** (A7).
   * This component only ever slices a growing prefix of this array for the client-side reveal - it never sorts
   * or otherwise reorders it.
   */
  instances: ComponentInstanceSummary[];
}

/**
 * Renders a group's instances as `InstanceRow`s (T10), revealing them **20 at a time** purely client-side over
 * data already held in memory (FR-008; architecture `02-architecture.md` §6.1, decision D7) - this component
 * never issues a request.
 *
 * The reveal count is deliberately **local** component state (architecture decision DP2): `InstanceList` owns
 * only the rows and the reveal count, while `SourceDocumentCard` (T11) owns solely the header and the
 * expand/collapse state, rendering this component through `children` only while expanded
 * (`{expanded && children}` in `SourceDocumentCard.tsx`). Because a collapsed card therefore unmounts its
 * children entirely rather than merely hiding them, the next expansion mounts a fresh `InstanceList` with its
 * `useState` re-initialized to `PAGE_SIZE` - that remount, not an explicit reset handler, is what satisfies the
 * "collapsing resets the reveal" rule (FR-008). Each mounted `InstanceList` keeps its own independent count, so
 * multiple expanded cards page independently by construction.
 *
 * @export
 * @param {InstanceListProps} props
 */
export const InstanceList: React.FC<InstanceListProps> = ({ instances }) => {
  const [revealedCount, setRevealedCount] = useState(PAGE_SIZE);

  // A7: never sort - slice a growing prefix of the document-order array exactly as received.
  const visibleInstances = instances.slice(0, revealedCount);
  const hasMoreToReveal = revealedCount < instances.length;

  const revealNextPage = () => setRevealedCount((current) => Math.min(current + PAGE_SIZE, instances.length));

  return (
    <div data-testid="instance-list">
      {visibleInstances.map((instance) => (
        <InstanceRow instance={instance} key={instance.instanceId} />
      ))}
      {/* FR-008: rendered only while unrevealed instances remain - never once every instance is visible, and
          never a request-issuing control (P6, D7) - it only grows the local slice bound. */}
      {hasMoreToReveal && (
        <button
          data-testid="instance-list-show-more"
          id={generateIdentifier(instances[0]?.instanceId ?? "", "showmore")}
          onClick={revealNextPage}
          style={{
            alignSelf: "flex-start",
            background: "none",
            border: "none",
            color: "var(--color-blue)",
            cursor: "pointer",
            marginTop: 6,
            padding: 0,
          }}
          type="button"
        >
          {formatMessage(CAPTION_SHOW_MORE)}
        </button>
      )}
    </div>
  );
};
